import { useState } from 'react';
import { Badge, Button } from '@indipia/ui-core';
import { formatReceivedAt, photoCountText } from '@indipia/shared-features';
import { PhotoLightbox } from '../PhotoLightbox';
import { ThumbImg } from '../ThumbImg';
import type { CustReqReportItem, CustReqReportSummary } from '../../types/custReqReport.types';
import { CUST_REQ_REPORT_MESSAGE as MSG, sortPhotos, summaryText, summaryTone } from './custReqReport.logic';

/**
 * 작업보고확인 카드의 고객 요청 블록 — 기획 004 D.
 * 접힌 상태는 '고객 요청 n · 반영 n'(모두 반영 초록 / 아니면 주황, 숫자 병기)만 보이고,
 * 펼치면 요청 원문 · 고객 사진을 보여 준다. 반영되지 않은 요청은 주황 줄 + [반영 처리].
 */

interface Props {
  items: CustReqReportItem[];
  summary: CustReqReportSummary;
  /** 사장님이 직접 쓴 보고면 '반장님 체크 없음' 대신 '체크 없음' */
  ownerReport: boolean;
  /** 지금 [반영 처리] 호출 중인 요청 ID */
  busyIds: ReadonlySet<number>;
  onApply: (custReqId: number) => void;
}

type PhotoLike = { url: string; thumbUrl: string; photoSeq: number };

/** 고객 사진 줄 — 사진이 1장 이상일 때만 그린다(정렬된 사진을 받는다) */
function PhotoStrip({
  label, photos, onOpen,
}: {
  label: string;
  photos: PhotoLike[];
  onOpen: (index: number) => void;
}) {
  return (
    <div className="min-w-0">
      <div className="text-caption text-text-muted">
        {label} {photoCountText(photos.length)}
      </div>
      <div className="mt-1 flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <button
            key={`${p.photoSeq}-${i}`}
            type="button"
            aria-label={`${label} ${i + 1} 크게 보기`}
            onClick={() => onOpen(i)}
            className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-surface-muted p-0"
          >
            <ThumbImg url={p.thumbUrl} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function CustReqReportBlock({ items, summary, ownerReport, busyIds, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [lb, setLb] = useState<{ photos: { url: string }[]; index: number } | null>(null);
  const tone = summaryTone(summary);

  return (
    <div className="px-4 pb-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="flex w-full items-center gap-2 rounded-md bg-surface-muted px-3 py-2 text-left"
      >
        <Badge status={tone}>{summaryText(summary)}</Badge>
        <span className="ml-auto text-caption font-bold text-info">{open ? '접기' : '펼치기'}</span>
      </button>

      {open && (
        <ul className="mt-2 space-y-2">
          {items.map((r, idx) => {
            const reqPhotos = sortPhotos(r.requestPhotos);
            const busy = busyIds.has(r.custReqId);
            return (
              <li key={r.custReqId} className="rounded-md border border-border p-3">
                <div className="flex items-start gap-2">
                  <span className="shrink-0 text-body-sm font-bold text-text">{idx + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm leading-relaxed text-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {r.content}
                    </p>
                    <p className="mt-1 text-caption text-text-muted">
                      {[r.srcName, formatReceivedAt(r.receivedAt) && `${formatReceivedAt(r.receivedAt)} 접수`].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>

                {reqPhotos.length > 0 && (
                  <div className="mt-2">
                    <PhotoStrip
                      label={MSG.customerPhoto}
                      photos={reqPhotos}
                      onOpen={(i) => setLb({ photos: reqPhotos.map((p) => ({ url: p.url || p.thumbUrl })), index: i })}
                    />
                  </div>
                )}

                {r.applied ? (
                  <div className="mt-2">
                    <Badge status="success">✓ {MSG.applied}</Badge>
                  </div>
                ) : (
                  <div
                    className="mt-2 flex items-center justify-between gap-2 rounded-md px-3 py-2"
                    style={{ background: 'var(--color-warningSubtle)' }}
                  >
                    <span className="text-body-sm font-medium" style={{ color: 'var(--color-warningStrong)' }}>
                      ⚠ {ownerReport ? MSG.noCheckOwner : MSG.noCheck}
                    </span>
                    {r.canApply && (
                      <Button size="sm" variant="secondary" disabled={busy} onClick={() => onApply(r.custReqId)}>
                        {busy ? MSG.applying : MSG.apply}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <PhotoLightbox
        photos={lb?.photos ?? []}
        index={lb?.index ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, index: i } : prev))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />
    </div>
  );
}
